// One message in a timeline — used by the room view and the thread view so they never drift apart.
import React, { useState } from 'react';
import { View, Pressable, Image, ActivityIndicator, StyleSheet } from 'react-native';
import { Text } from './Text';
import { MEvent, Room, mediaSource, senderName, decisionOn, waits, GateChoice } from '../matrix';
import { RichText } from '../RichText';
import { Avatar } from './Avatar';
import { HelmCard } from './Card';
import { DECIDED } from './GateBar';
import { radius, makeStyles, useTheme } from '../theme';

function MessageRowInner({ item, room, userId, cont, isAgent, humanBadge, quoted, hideQuote, onLongPress, onOpenImage, onOpenFile, opening, thread, onOpenThread, onCardAction, footer }: MessageRowProps) {
  const theme = useTheme(); const s = useStyles();
  const mine = item.sender === userId;
  const name = senderName(room, item.sender);
  const agent = isAgent(item.sender);
  const [useLocal, setUseLocal] = useState(!!item.localUri); // our own copy first; if that file is gone, fall back to the server
  const thumb = item.kind === 'image' ? (useLocal && item.localUri ? { uri: item.localUri } : mediaSource(item.mxc, { w: 480, h: 480 })) : null;
  const [imgErr, setImgErr] = useState<string | null>(null);
  const [imgTry, setImgTry] = useState(0);
  // A card that waits carries the whole request; the sentence next to it is for clients that cannot draw cards.
  // Once the agent closes it (approved, denied), its sentence says what happened, so that one is shown.
  const approval = !!item.card && !item.card.envelope && String(item.card.type || '').toLowerCase() === 'approval';
  const decided = approval ? decisionOn(item) : undefined;
  // A hand-off card's second line is the message itself. With the message in full right above the card, a copy
  // of it cut after a few words ("fixed. Cause was R…") only made people wonder what was missing.
  const flat = (t: unknown) => String(t ?? '').replace(/\s+/g, ' ').trim();
  const repeats = !!item.card?.envelope && !!item.card.subtitle && !!item.body && flat(item.body).includes(flat(item.card.subtitle));
  const base = repeats ? { ...item.card, subtitle: undefined } : item.card;
  const card = decided ? { ...base, status: STATUS[decided] } : base;
  // A person's decision on a card: said in words, not as the command that was sent.
  const verdict = item.decision && DECIDED[item.decision.decision as GateChoice] ? item.decision : undefined;
  return (
    <Pressable onLongPress={onLongPress ? () => onLongPress(item) : undefined} delayLongPress={300} style={[s.msg, cont && { marginTop: 2 }, item.pending && { opacity: 0.55 }]}>
      {!cont ? <Avatar name={name} mxc={room?.memberAvatars[item.sender]} kind={agent ? 'agent' : 'human'} size={34} humanBadge={humanBadge} me={mine} /> : <View style={{ width: 34 }} />}
      <View style={{ flex: 1, minWidth: 0 }}>
        {!cont && (
          <View style={s.head}>
            <Text style={[s.name, mine && { color: theme.accent }, agent && { color: theme.agentText }]}>{mine ? 'You' : name}</Text>
            <Text style={s.time}>{fmtTime(item.ts)}</Text>
          </View>
        )}
        {item.replyTo && !hideQuote && !verdict && (
          <View style={s.quote}>
            <Text style={s.quoteWho} numberOfLines={1}>↩ {quoted ? (quoted.sender === userId ? 'You' : senderName(room, quoted.sender)) : 'Reply'}</Text>
            <Text style={s.quoteText} numberOfLines={2}>{quoted ? (quoted.kind === 'image' ? '📷 Photo' : quoted.kind === 'file' ? `📎 ${quoted.body}` : quoted.body) : 'Earlier message'}</Text>
          </View>
        )}
        {verdict ? (
          <View style={s.verdict}>
            <Text style={[s.verdictWhat, verdict.decision === 'deny' && { color: theme.deny }, verdict.decision === 'revise' && { color: theme.ink }]}>{DECIDED[verdict.decision]}</Text>
            {!!quoted?.card?.title && <Text style={s.verdictAbout} numberOfLines={1}>{String(quoted.card.title)}</Text>}
            {!!verdict.note && <Text style={s.body}>{verdict.note}</Text>}
          </View>
        ) : item.thread ? (
          // The start of a conversation opened on purpose: a labelled title, not a raw notice line.
          <View style={s.convo}>
            <Text style={s.convoLabel}>{({ conversation: 'Conversation', build: 'Build', fix: 'Fix' } as Record<string, string>)[item.thread.kind] || 'Thread'}</Text>
            <Text style={s.convoTitle}>{item.thread.title || item.body}</Text>
          </View>
        ) : item.kind === 'encrypted' ? (
          <Text style={s.locked}>🔒 {item.body}</Text>
        ) : item.kind === 'image' && thumb ? (
          imgErr ? (
            // Say why instead of leaving a blank square; tapping retries the download.
            <Pressable onPress={() => { setImgErr(null); setImgTry((n) => n + 1); }} style={[s.image, s.imageErr]}>
              <Text style={s.imageErrText}>📷 Photo couldn't load</Text>
              <Text style={s.imageErrHint} numberOfLines={3}>{imgErr}</Text>
              <Text style={s.imageErrHint}>Tap to retry</Text>
            </Pressable>
          ) : (
            <Pressable onPress={onOpenImage ? () => onOpenImage(item) : undefined} onLongPress={onLongPress ? () => onLongPress(item) : undefined}>
              <Image key={imgTry} source={thumb} style={s.image} resizeMode="cover" onError={(e) => { if (useLocal) setUseLocal(false); else setImgErr(e.nativeEvent?.error || 'download failed'); }} />
            </Pressable>
          )
        ) : item.kind === 'file' ? (
          <Pressable onPress={onOpenFile ? () => onOpenFile(item) : undefined} style={s.fileChip} disabled={opening === item.id}>
            {opening === item.id ? <ActivityIndicator color={theme.accent} size="small" /> : <Text style={s.fileText} numberOfLines={1}>📎 {item.body}</Text>}
          </Pressable>
        ) : (
          !!item.body && !(approval && waits(item.card.status)) && <RichText html={item.html} text={item.body} style={s.body} />
        )}
        {item.card && <HelmCard card={card} onAction={onCardAction} />}
        {thread && (thread.count > 0 || item.thread) && (
          <Pressable onPress={onOpenThread ? () => onOpenThread(item) : undefined} style={({ pressed }) => [s.threadChip, pressed && { opacity: 0.6 }]}>
            <Text style={s.threadText}>{item.thread?.kind === 'conversation' ? 'Open conversation' : 'View thread'}</Text>
            <Text style={s.threadMeta}>{thread.count === 0 ? 'no replies yet' : `${thread.count} ${thread.count === 1 ? 'reply' : 'replies'}`}{thread.latestTs ? ` · ${fmtTime(thread.latestTs)}` : ''}</Text>
            {!!thread.unread && <Text style={s.threadNew}>{thread.unread} new</Text>}
            <Text style={s.threadChev}>›</Text>
          </Pressable>
        )}
        {footer}
      </View>
    </Pressable>
  );
}

type MessageRowProps = {
  item: MEvent; room?: Room; userId: string | null; cont: boolean;
  isAgent: (uid: string) => boolean; humanBadge: boolean;
  quoted?: MEvent; hideQuote?: boolean; onLongPress?: (ev: MEvent) => void;
  onOpenImage?: (ev: MEvent) => void; onOpenFile?: (ev: MEvent) => void; opening?: string | null;
  thread?: { count: number; latestTs?: number; unread?: number } | null; onOpenThread?: (ev: MEvent) => void;
  onCardAction?: (value: string, label: string) => void;
  footer?: React.ReactNode;
  /** What the footer shows, as a string (reactions, gate state, seen). The footer element is new on every render,
   *  so the memo compares this key instead. Leave it out and the row redraws whenever a footer is passed. */
  footerKey?: string;
};

/** Rows are memoized: a message redraws only when what it shows changes. `room` is compared by what the row reads
 *  from it (the sender's name and avatar), `thread` by its numbers, the footer by `footerKey`. */
export const MessageRow = React.memo(MessageRowInner, (a, b) =>
  a.item === b.item && a.userId === b.userId && a.cont === b.cont && a.humanBadge === b.humanBadge &&
  a.quoted === b.quoted && !!a.hideQuote === !!b.hideQuote && (a.opening === a.item.id) === (b.opening === b.item.id) &&
  (a.thread?.count ?? -1) === (b.thread?.count ?? -1) && (a.thread?.latestTs ?? 0) === (b.thread?.latestTs ?? 0) && (a.thread?.unread ?? 0) === (b.thread?.unread ?? 0) &&
  a.onLongPress === b.onLongPress && a.onOpenImage === b.onOpenImage && a.onOpenFile === b.onOpenFile &&
  a.onOpenThread === b.onOpenThread && a.onCardAction === b.onCardAction &&
  (a.footerKey !== undefined || b.footerKey !== undefined ? a.footerKey === b.footerKey : a.footer === b.footer) &&
  senderName(a.room, a.item.sender) === senderName(b.room, b.item.sender) &&
  a.room?.memberAvatars[a.item.sender] === b.room?.memberAvatars[b.item.sender] &&
  a.isAgent(a.item.sender) === b.isAgent(b.item.sender) &&
  (!a.quoted || senderName(a.room, a.quoted.sender) === senderName(b.room, a.quoted.sender)));

const STATUS: Record<GateChoice, string> = { approve: 'approved', always: 'approved', revise: 'sent back', deny: 'denied' };

export function fmtTime(ts: number) {
  return new Date(ts).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}
export function dayLabel(ts: number) {
  const d = new Date(ts); const now = new Date(); const y = new Date(now); y.setDate(now.getDate() - 1);
  if (d.toDateString() === now.toDateString()) return 'Today';
  if (d.toDateString() === y.toDateString()) return 'Yesterday';
  return d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
}

const useStyles = makeStyles((theme) => StyleSheet.create({
  msg: { flexDirection: 'row', gap: 10, marginTop: 10 },
  head: { flexDirection: 'row', alignItems: 'baseline', gap: 8 },
  name: { color: theme.ink, fontWeight: '700', fontSize: 14 },
  time: { color: theme.faint, fontSize: 11, fontFamily: theme.mono },
  body: { color: theme.ink, fontSize: 15, lineHeight: 22.5, letterSpacing: -0.15, marginTop: 1 },
  locked: { color: theme.faint, fontSize: 14, fontStyle: 'italic', marginTop: 1 },
  image: { width: 220, height: 220, borderRadius: radius.md, marginTop: 6, backgroundColor: theme.surface2 },
  imageErr: { alignItems: 'center', justifyContent: 'center', padding: 12, borderWidth: 1, borderColor: theme.border, gap: 4 },
  imageErrText: { color: theme.ink, fontSize: 14, fontWeight: '700' },
  imageErrHint: { color: theme.faint, fontSize: 12, textAlign: 'center' },
  fileChip: { marginTop: 6, backgroundColor: theme.surface2, borderRadius: radius.sm, paddingHorizontal: 10, paddingVertical: 8, alignSelf: 'flex-start', maxWidth: 240, minWidth: 60 },
  fileText: { color: theme.ink, fontSize: 13 },
  quote: { borderLeftWidth: 2, borderLeftColor: theme.accent, paddingLeft: 8, marginTop: 2, marginBottom: 4 },
  quoteWho: { color: theme.accent, fontSize: 11, fontWeight: '700' },
  quoteText: { color: theme.muted, fontSize: 12.5 },
  threadChip: { flexDirection: 'row', alignItems: 'center', gap: 6, alignSelf: 'flex-start', marginTop: 8, backgroundColor: theme.accentBg, borderRadius: radius.pill, paddingHorizontal: 12, paddingVertical: 6, borderWidth: 1, borderColor: theme.accentBorder },
  threadText: { color: theme.accent, fontSize: 13, fontWeight: '700' },
  threadMeta: { color: theme.muted, fontSize: 12 },
  threadNew: { color: theme.accent, fontSize: 12, fontWeight: '800' },
  threadChev: { color: theme.accent, fontSize: 16, lineHeight: 18, fontWeight: '700' },
  verdict: { marginTop: 2, gap: 2 },
  verdictWhat: { color: theme.approve, fontSize: 14, fontWeight: '700' },
  verdictAbout: { color: theme.muted, fontSize: 12.5 },
  convo: { marginTop: 2, paddingVertical: 8, paddingHorizontal: 12, backgroundColor: theme.surface, borderWidth: 1, borderColor: theme.border, borderRadius: radius.md, alignSelf: 'flex-start', maxWidth: '100%' },
  convoLabel: { color: theme.faint, fontSize: 10.5, fontWeight: '700', letterSpacing: 1, textTransform: 'uppercase', fontFamily: theme.mono },
  convoTitle: { color: theme.ink, fontSize: 15, fontWeight: '700', marginTop: 2 },
}));
